import { useItemContacts } from "@/hooks/useItemContacts";
import { User, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ItemPeoplePreviewProps {
  itemId: string;
}

const roleLabels: Record<string, string> = {
  applicant: "Applicant",
  dependent: "Dependente",
  beneficiary: "Beneficiário",
  petitioner: "Peticionário",
};

const relationshipLabels: Record<string, string> = {
  spouse: "Cônjuge",
  child: "Filho(a)",
  parent: "Pai/Mãe",
  sibling: "Irmão/Irmã",
};

export default function ItemPeoplePreview({ itemId }: ItemPeoplePreviewProps) {
  const { contacts, isLoading } = useItemContacts(itemId);

  if (isLoading) {
    return (
      <div className="mt-2 text-xs text-slate-400">Carregando...</div>
    );
  }

  if (!contacts || contacts.length === 0) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
        <AlertTriangle className="w-3 h-3" />
        <span>Nenhuma pessoa associada</span>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1">
      {contacts.slice(0, 3).map((itemContact) => {
        const contact = itemContact.contact;
        if (!contact) return null;
        
        const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Sem nome";
        const isPayer = itemContact.is_payer;
        const immigrationRole = itemContact.immigration_role;
        const relationshipType = itemContact.relationship_type;
        
        return (
          <div key={itemContact.id} className="flex items-center gap-1.5 text-xs text-slate-600">
            <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="truncate">{name}</span>
            {immigrationRole && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-blue-50 border-blue-200 text-blue-700">
                {roleLabels[immigrationRole] || immigrationRole}
              </Badge>
            )}
            {immigrationRole === "dependent" && relationshipType && (
              <span className="text-slate-400">
                ({relationshipLabels[relationshipType] || relationshipType})
              </span>
            )}
            {isPayer && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-emerald-50 border-emerald-200 text-emerald-700">
                Payer
              </Badge>
            )}
          </div>
        );
      })}
      {contacts.length > 3 && (
        <div className="text-xs text-slate-400">
          e mais {contacts.length - 3}...
        </div>
      )}
    </div>
  );
}
